/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { generateTestId } from '@/utils/shared/generateTestIds';

interface FilterItem {
  id: string;
  label: string;
  checked?: boolean;
  disabled?: boolean;
}

interface FilterGroup {
  id: string;
  title?: string;
  label?: string;
  type?: string;
  items?: FilterItem[];
  // Legacy support
  options?: FilterItem[];
  isMultiSelected?: boolean;
}

interface GroupDataLayerFiltersProps {
  filter: {
    id: string;
    label?: string;
    title?: string;
    type: string;
    groups?: FilterGroup[];
    // Legacy support
    options?: FilterGroup[];
    isMultiSelected?: boolean;
    handler?: (value: string | string[] | Record<string, string[]>, filterConfig?: any) => void;
    resetHandler?: (resetCallback: () => void) => void; // Callback to register reset function
  };
  onFilterChange?: (groupId: string, values: string[] | Record<string, string[]>) => void;
}

export const GroupDataLayerFilters: React.FC<GroupDataLayerFiltersProps> = ({
  filter,
  onFilterChange,
}: GroupDataLayerFiltersProps) => {
  const [open, setOpen] = useState(false);

  // Support both 'groups' and 'options' structures
  const groups = filter?.groups || filter?.options || [];

  // Store initial selections for reset functionality
  const initialSelectionsRef = React.useRef<Record<string, string[]> | null>(null);

  // Track the applied selections
  const [appliedSelections, setAppliedSelections] = useState<Record<string, string[]>>(() => {
    const initial: Record<string, string[]> = {};
    groups.forEach((group) => {
      const items = group.items || group.options || [];
      if (items.length > 0) {
        initial[group.id] = items.filter((item) => item.checked).map((item) => item.id);
      }
    });
    // Store initial state for reset
    initialSelectionsRef.current = initial;
    return initial;
  });

  // Track temporary selections (not yet applied)
  const [tempSelections, setTempSelections] = useState<Record<string, string[]>>(appliedSelections);

  const filterLabel = filter.label || filter.title || 'Filters';

  // Register reset handler if provided
  React.useEffect(() => {
    if (filter.resetHandler) {
      filter.resetHandler(() => {
        // Reset to initial state
        if (initialSelectionsRef.current) {
          setAppliedSelections(initialSelectionsRef.current);
          setTempSelections(initialSelectionsRef.current);
          // Apply the reset state through the handler
          if (filter.handler) {
            filter.handler(initialSelectionsRef.current, filter);
          }
        }
      });
    }
  }, [filter]);

  const handleChange = (groupId: string, itemId: string, checked: boolean) => {
    const newSelections = { ...tempSelections };
    if (checked) {
      newSelections[groupId] = [...(newSelections[groupId] || []), itemId];
    } else {
      newSelections[groupId] = newSelections[groupId].filter((id) => id !== itemId);
    }
    setTempSelections(newSelections);
  };

  const handleApply = () => {
    // Apply the temporary selections
    setAppliedSelections(tempSelections);

    // Call the filter's handler if it exists
    if (filter.handler) {
      filter.handler(tempSelections, filter);
    }

    // Also call onFilterChange for backward compatibility
    if (onFilterChange) {
      onFilterChange(filter.id, tempSelections);
    }

    // Close the popover
    setOpen(false);
  };

  const totalSelected = Object.values(appliedSelections).flat().length;

  if (!filter || groups.length === 0) {
    return null;
  }

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        if (newOpen) {
          // Reset temp selections to applied when opening
          setTempSelections(appliedSelections);
        }
        setOpen(newOpen);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="ghost" className="py-0" data-testid={`graph_control_${generateTestId(filterLabel)}`}>
          <span className="flex items-center text-sm font-semibold gap-2">
            {filterLabel}
            <ChevronDown className="w-4 h-4" />
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-80 p-3">
        {groups.map((group, index) => {
          const items = group.items || group.options || [];
          const groupTitle = group.title || group.label || group.id;

          return (
            <div key={group.id}>
              {index > 0 && <div className="my-3 border-t" />}
              <div className="mb-2 font-medium text-sm">{groupTitle}</div>
              <div className="space-y-1">
                {items.map((item) => {
                  const isChecked = tempSelections[group.id]?.includes(item.id) || false;
                  const isDisabled = item.disabled === true;
                  return (
                    <label
                      data-testid={`menu_item_${generateTestId(item.id)}`}
                      key={item.id}
                      className={`flex items-center gap-2 p-1 ${
                        isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'
                      }`}
                    >
                      <Checkbox
                        checked={isChecked}
                        disabled={isDisabled}
                        onCheckedChange={(checked) => {
                          if (!isDisabled) {
                            handleChange(group.id, item.id, !!checked);
                          }
                        }}
                      />
                      <span className={`text-sm ${isDisabled ? 'text-gray-400' : ''}`}>{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className="mt-3 pt-3 border-t flex justify-between">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              // Reset temp selections to applied selections and close
              setTempSelections(appliedSelections);
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const cleared: Record<string, string[]> = {};
                groups.forEach((g) => {
                  if (g && g.id) {
                    cleared[g.id] = [];
                  }
                });
                setTempSelections(cleared);
                // Don't apply immediately - let user click Apply
              }}
            >
              Clear All
            </Button>
            <Button
              size="sm"
              variant="default"
              className="bg-primary-500 hover:bg-primary-600 text-white"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
